import React, { useState, useMemo } from 'react';
import { Icon } from '@iconify/react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useModule } from '../utils/useModule';
import FormLivePreview from './FormLivePreview';

const FormDetailsTab = ({
  active,
  formItems,
  formInputTypes,
  selectStyles,
  itemsContainerRef,
  addItem,
  handleDragStartItem,
  handleDropItem,
  handleItemChange,
  saveItemField,
  handleImagesUpload,
  handleRemoveImage,
  handleDeleteItem,
  renderPreviewInput,
  getInputType,
  newForm,
  onEditHeader,
  onDiscardForm,
  editingId,
  onCopyForm = () => { },
  isCopying = false,
  canCopyForm = false,
}) => {
  const [listModalItem, setListModalItem] = useState(null);
  const [listValues, setListValues] = useState([]);
  const navigate = useNavigate();

  const planthireEnabled = useModule('planthire');
  const filteredFormInputTypes = useMemo(() => {
    const types = planthireEnabled
      ? formInputTypes
      : formInputTypes.filter(
        (t) => !['selectcustomer', 'selectproject'].includes(t.inputType)
      );

    return [...types].sort(
      (a, b) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER)
    );
  }, [formInputTypes, planthireEnabled]);

  const parseFormInputTypeData = (typeData) => {
    if (typeData === undefined || typeData === null) return null;
    if (typeof typeData === 'string') {
      try {
        return JSON.parse(typeData);
      } catch (error) {
        return typeData;
      }
    }
    return typeData;
  };

  const parseItemDataObject = (data) => {
    const parsed = parseFormInputTypeData(data);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
    return {};
  };

  const handleFormInputTypeChange = (itemId, nextFormInputTypeId) => {
    const selectedType = formInputTypes.find(
      (type) => type.id === nextFormInputTypeId
    );
    const parsedData = parseFormInputTypeData(selectedType?.data);
    const isPhotoType =
      (selectedType?.inputType || selectedType?.name || '')
        .toLowerCase() === 'photo';
    const isTextInputType = ['textbox', 'text'].includes(
      (selectedType?.inputType || selectedType?.name || '').toLowerCase()
    );
    const nextData = isPhotoType ? { minimumPhotos: '0' } : parsedData;
    handleItemChange(itemId, 'formInputTypeId', nextFormInputTypeId);
    handleItemChange(itemId, 'data', nextData);
    if (isTextInputType) {
      handleItemChange(itemId, 'disableComments', 1);
    }
    const nextFields = {
      formInputTypeId: nextFormInputTypeId,
      data: isPhotoType ? nextData : selectedType?.data ?? null,
      ...(isTextInputType ? { disableComments: 1 } : {}),
    };
    saveItemField(itemId, nextFields);
  };

  const openListModal = (item) => {
    setListModalItem(item);
    const items = Array.isArray(item.data) ? item.data : [];
    setListValues(items);
  };

  const closeListModal = () => {
    setListModalItem(null);
    setListValues([]);
  };

  const handleAddListValue = () => {
    if (listValues.length >= 10) return;
    setListValues((prev) => [...prev, '']);
  };

  const handleListValueChange = (idx, value) => {
    setListValues((prev) => prev.map((v, i) => (i === idx ? value : v)));
  };

  const handleDeleteListValue = (idx) => {
    setListValues((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSaveListValues = () => {
    if (!listModalItem) return;
    handleItemChange(listModalItem.id, 'data', listValues);
    saveItemField(listModalItem.id, { data: listValues });
    closeListModal();
  };

  return (
    <div className={`tab-pane fade ${active ? 'show active' : ''}`} role="tabpanel">
      <div className="row">
        <div className="col-md-7 p-0 d-flex flex-column">
          <div className="d-flex mt-24 mb-12 justify-content-between align-items-center">
            <h5 className="ms-8 mb-0">{newForm.name}</h5>
          </div>
          <div className="border radius-8 pt-16">
            <div
              className="d-flex justify-content-between align-items-center pb-12 bg-white border-bottom"
              style={{ position: 'sticky', bottom: 0, zIndex: 1 }}
            >
              <div className="d-flex gap-2 ms-16">
                <button className="btn btn-outline-primary" onClick={onEditHeader}>
                  <Icon
                    icon="mdi:cog-outline"
                    className="text-xl me-6"
                    style={{ position: 'relative', top: '-1px' }}
                  />
                  Form Settings
                </button>
              </div>
              <div className="d-flex gap-3 me-16 align-items-center">
                <div className="d-flex gap-2">
                  <button className="btn btn-primary" onClick={() => addItem(1)}>
                    + Add Heading
                  </button>
                  <button className="btn btn-primary" onClick={() => addItem(0)}>
                    + Add Item
                  </button>
                </div>
                {editingId && (
                  <div className="dropdown">
                    <button
                      type="button"
                      className="btn text-primary-600 hover-text-primary px-18 show border-0 text-xxl py-4 my-6"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <Icon
                        icon="mdi:dots-vertical"
                        className="text-primary"
                        style={{ position: 'relative', top: '-5px' }}
                      />
                    </button>
                    <ul className="dropdown-menu dropdown-menu-end mt-12">
                      {canCopyForm && (
                        <li>
                          <button
                            type="button"
                            className="dropdown-item py-12"
                            onClick={() => onCopyForm?.(editingId)}
                            disabled={isCopying}
                          >
                            {isCopying ? 'Copying…' : 'Copy Form'}
                          </button>
                        </li>
                      )}
                      <li>
                        <button
                          type="button"
                          className="dropdown-item text-danger py-12"
                          onClick={onDiscardForm}
                        >
                          Discard Form
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div
              style={{ height: 'calc(100vh - 230px)', overflowY: 'auto' }}
              ref={itemsContainerRef}
            >
              {formItems.map((item) => {
                const selectedFormInputType = formInputTypes.find(
                  (type) => type.id === item.formInputTypeId
                );
                const selectedInputTypeKey = (selectedFormInputType?.inputType || '').toLowerCase();
                const itemInputType = getInputType(item.formInputTypeId);
                const isPhotoInput = itemInputType === 'photo';
                const parsedItemData = isPhotoInput
                  ? parseItemDataObject(item.data)
                  : {};
                const minimumPhotosValue = isPhotoInput
                  ? String(
                    parsedItemData.minimumPhotos === undefined ||
                      parsedItemData.minimumPhotos === null
                      ? '0'
                      : parsedItemData.minimumPhotos
                  )
                  : '0';
                const normalizedMinimumPhotosValue =
                  minimumPhotosValue === 'N/A' ? '0' : minimumPhotosValue;

                let selectedInputTypeData = selectedFormInputType?.data;
                if (typeof selectedInputTypeData === 'string') {
                  try {
                    selectedInputTypeData = JSON.parse(selectedInputTypeData);
                  } catch (error) {
                    selectedInputTypeData = {};
                  }
                }
                if (!selectedInputTypeData || typeof selectedInputTypeData !== 'object') {
                  selectedInputTypeData = {};
                }

                const raiseDefectTriggerLabel = selectedInputTypeData.raisedefect;
                const showAutoRaiseToggle = ['passfail', 'yesno', 'okdefect'].includes(
                  selectedInputTypeKey
                );
                const formInputSelectColClass = isPhotoInput
                  ? 'col-md-3'
                  : `col-md-${showAutoRaiseToggle ? 3 : 5}`;

                return (
                  <div
                    key={item.id}
                    className="card mb-3 w-90 mx-12 mt-12"
                    style={{ boxShadow: '0 0.25rem 1rem rgba(46,46,46,0.25)' }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDropItem(item.id)}
                  >
                    <div className="card-body">
                      <div className="d-flex justify-content-between">
                        <div className="w-100">
                          <div className="row g-3">
                            {item.heading === 0 ? (
                              <>
                                <div className="col-md-1 d-flex align-items-start pt-2">
                                  <Icon
                                    className="fs-4 mt-6"
                                    icon="ic:baseline-menu"
                                    draggable
                                    onDragStart={() => handleDragStartItem(item.id)}
                                  />
                                </div>
                                <div className="col-md-5">
                                  <input
                                    className="form-control"
                                    value={item.description || ''}
                                    placeholder="Description"
                                    onChange={(e) =>
                                      handleItemChange(item.id, 'description', e.target.value)
                                    }
                                    onBlur={() =>
                                      saveItemField(item.id, { description: item.description })
                                    }
                                  />
                                </div>
                                {itemInputType === 'dropdown' || itemInputType === 'dropdownmulti' ? (
                                  <>
                                    <div className="col-md-3">
                                      <Select
                                        classNamePrefix="react-select"
                                        styles={selectStyles}
                                        value={filteredFormInputTypes
                                          .map((t) => ({ value: t.id, label: t.name, icon: t.icon }))
                                          .find((opt) => opt.value === item.formInputTypeId) || null}
                                        onChange={(opt) => {
                                          const nextId = opt?.value || 1;
                                          handleFormInputTypeChange(item.id, nextId);
                                        }}
                                        options={filteredFormInputTypes.map((t) => ({
                                          value: t.id,
                                          label: t.name,
                                          icon: t.icon,
                                        }))}
                                        formatOptionLabel={(opt) => (
                                          <div className="d-flex align-items-center gap-2">
                                            <Icon icon={opt.icon} /> {opt.label}
                                          </div>
                                        )}
                                      />
                                    </div>
                                    <div className="col-md-2 d-flex justify-content-end">
                                      <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={() => openListModal(item)}
                                      >
                                        List Items
                                      </button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className={formInputSelectColClass}>
                                      <Select
                                        classNamePrefix="react-select"
                                        styles={selectStyles}
                                        value={filteredFormInputTypes
                                          .map((t) => ({ value: t.id, label: t.name, icon: t.icon }))
                                          .find((opt) => opt.value === item.formInputTypeId) || null}
                                        onChange={(opt) => {
                                          const nextId = opt?.value || 1;
                                          handleFormInputTypeChange(item.id, nextId);
                                        }}
                                        options={filteredFormInputTypes.map((t) => ({
                                          value: t.id,
                                          label: t.name,
                                          icon: t.icon,
                                        }))}
                                        formatOptionLabel={(opt) => (
                                          <div className="d-flex align-items-center gap-2">
                                            <Icon icon={opt.icon} /> {opt.label}
                                          </div>
                                        )}
                                      />
                                    </div>
                                    {isPhotoInput && (
                                      <div className="col-md-2 d-flex align-items-center gap-2 mt-10">
                                        <label className="form-label mb-0">Min # Photos</label>
                                        <select
                                          className="form-select flex-grow-1"
                                          value={normalizedMinimumPhotosValue}
                                          onChange={(e) => {
                                            const selectedValue = e.target.value;
                                            const storedValue = selectedValue === '0'
                                              ? '0'
                                              : selectedValue;
                                            const nextData = {
                                              ...parsedItemData,
                                              minimumPhotos: storedValue,
                                            };
                                            handleItemChange(item.id, 'data', nextData);
                                            saveItemField(item.id, { data: nextData });
                                          }}
                                        >
                                          <option value="0">0</option>
                                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map((num) => (
                                            <option key={num} value={String(num)}>
                                              {num}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    )}
                                    {showAutoRaiseToggle && (
                                      <div className="col-md-2">
                                        <div className="d-flex align-items-center mt-10">
                                          <label
                                            className="form-label mb-0 me-16"
                                            title={
                                              raiseDefectTriggerLabel
                                                ? `Automatically raises on "${raiseDefectTriggerLabel}"`
                                                : undefined
                                            }
                                          >
                                            Request
                                          </label>
                                          <div className="form-switch switch-primary">
                                            <input
                                              className="form-check-input"
                                              type="checkbox"
                                              checked={Boolean(item.raiseDefects)}
                                              onChange={(e) => {
                                                const value = e.target.checked ? 1 : 0;
                                                handleItemChange(item.id, 'raiseDefects', value);
                                                saveItemField(item.id, { raiseDefects: value });
                                              }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                                <div className="col-md-1"></div>
                                <div className="col-md-1 mb-8"></div>
                                <div className="col-md-10 mb-8">
                                  <textarea
                                    className="form-control"
                                    value={item.instructions || ''}
                                    placeholder="Instructions"
                                    onChange={(e) =>
                                      handleItemChange(item.id, 'instructions', e.target.value)
                                    }
                                    onBlur={() =>
                                      saveItemField(item.id, { instructions: item.instructions })
                                    }
                                  />
                                </div>
                                <div className="col-md-1 mb-8"></div>
                                <div className="col-md-1 mb-16"></div>
                                <div className="col-md-4 mb-16">
                                  <div className="d-flex align-items-center mt-10">
                                    <label className="form-label mb-0 me-16">Disable Comments</label>
                                    <div className="form-switch switch-primary">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={Boolean(item.disableComments)}
                                        onChange={(e) => {
                                          handleItemChange(
                                            item.id,
                                            'disableComments',
                                            e.target.checked ? 1 : 0
                                          );
                                          saveItemField(item.id, {
                                            disableComments: e.target.checked ? 1 : 0,
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="col-md-4 mb-16">
                                  {isPhotoInput ? (
                                    <div
                                      className="d-flex align-items-center mt-10"
                                      style={{ visibility: 'hidden' }}
                                    >
                                      <label className="form-label mb-0 me-16">Allow Photos</label>
                                      <div className="form-switch switch-primary">
                                        <input className="form-check-input" type="checkbox" />
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="d-flex align-items-center mt-10">
                                      <label className="form-label mb-0 me-16">Allow Photos</label>
                                      <div className="form-switch switch-primary">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={Boolean(item.includePhoto)}
                                          onChange={(e) => {
                                            handleItemChange(
                                              item.id,
                                              'includePhoto',
                                              e.target.checked ? 1 : 0
                                            );
                                            saveItemField(item.id, {
                                              includePhoto: e.target.checked ? 1 : 0,
                                            });
                                          }}
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="col-md-3 mb-16">
                                  {itemInputType !== 'none' && (
                                    <>
                                      <div className="d-flex align-items-center mt-10">
                                        <label className="form-label mb-0 me-16">Required</label>
                                        <div className="form-switch switch-primary">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={Boolean(item.required)}
                                            onChange={(e) => {
                                              handleItemChange(item.id, 'required', e.target.checked ? 1 : 0);
                                              saveItemField(item.id, { required: e.target.checked ? 1 : 0 });
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                                <div className="col-md-1"></div>
                                <div className="col-md-7">
                                  <input
                                    type="file"
                                    multiple
                                    placeholder="Upload Images"
                                    className="form-control"
                                    onChange={(e) => handleImagesUpload(item.id, e.target.files)}
                                  />
                                  {Array.isArray(item.images) && item.images.length > 0 && (
                                    <div className="mt-2 d-flex flex-wrap gap-2 mt-12">
                                      {item.images.map((img, idx) => (
                                        <div
                                          key={idx}
                                          style={{ position: 'relative', width: '105px', height: '105px' }}
                                        >
                                          <img
                                            src={`${process.env.REACT_APP_SPACES_BASE}/${img}`}
                                            alt="form"
                                            style={{
                                              width: '100%',
                                              height: '100%',
                                              objectFit: 'cover',
                                              borderRadius: '8px',
                                            }}
                                          />
                                          <button
                                            type="button"
                                            onClick={() => handleRemoveImage(item.id, idx)}
                                            style={{
                                              position: 'absolute',
                                              top: '-6px',
                                              right: '-6px',
                                              width: '20px',
                                              height: '20px',
                                              borderRadius: '50%',
                                              border: 'none',
                                              background: '#dc3545',
                                              color: '#fff',
                                              cursor: 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              padding: 0,
                                              lineHeight: 1,
                                            }}
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="col-md-3">
                                  <Select
                                    classNamePrefix="react-select"
                                    styles={selectStyles}
                                    value={[
                                      { value: 4, label: '25% Width' },
                                      { value: 6, label: '50% Width' },
                                      { value: 8, label: '75% Width' },
                                      { value: 12, label: '100% Width' },
                                    ].find((w) => w.value === item.width) || null}
                                    onChange={(opt) => {
                                      handleItemChange(item.id, 'width', opt.value);
                                      saveItemField(item.id, { width: opt.value });
                                    }}
                                    options={[
                                      { value: 4, label: '25%' },
                                      { value: 6, label: '50%' },
                                      { value: 8, label: '75%' },
                                      { value: 12, label: '100%' },
                                    ]}
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="col-md-1 d-flex align-items-start pt-2">
                                  <Icon
                                    className="fs-4 mt-6"
                                    icon="ic:baseline-menu"
                                    draggable
                                    onDragStart={() => handleDragStartItem(item.id)}
                                  />
                                </div>
                                <div className="col-md-10">
                                  <input
                                    className="form-control"
                                    value={item.description || ''}
                                    onChange={(e) =>
                                      handleItemChange(item.id, 'description', e.target.value)
                                    }
                                    onBlur={() => saveItemField(item.id, { description: item.description })}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="col-md-5">
          <div className="d-flex justify-content-between align-items-center ms-6 mb-8 mt-12">
            <h5 className="mb-0 text-xl">Live Preview</h5>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => navigate('/forms')}
            >
              Back to Main Menu
            </button>

          </div>
          <FormLivePreview
            form={newForm}
            items={formItems}
            renderPreviewInput={renderPreviewInput}
            onClose={() => navigate('/forms')}
            closeLabel="Return to Forms List"
          />
        </div>
      </div>
      {listModalItem && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">List Items</h5>
                <button type="button" className="btn-close" onClick={closeListModal}></button>
              </div>
              <div className="modal-body">
                <div className="d-flex justify-content-end mb-12">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleAddListValue}
                    disabled={listValues.length >= 10}
                  >
                    + Add Item
                  </button>
                </div>

                <div className="list-group">
                  {listValues.map((val, idx) => (
                    <div
                      key={idx}
                      className="list-group-item d-flex align-items-center gap-2"
                    >
                      <input
                        type="text"
                        className="form-control"
                        value={val}
                        onChange={(e) => handleListValueChange(idx, e.target.value)}
                      />
                      <button
                        type="button"
                        className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                        onClick={() => handleDeleteListValue(idx)}
                      >
                        <Icon
                          icon="ic:baseline-delete-outline"
                          className="icon text-xl line-height-1"
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeListModal}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSaveListValues}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

  );
};

export default FormDetailsTab;
